<template>
  <BaseContainer class="p-6">
    <div
      v-if="instances.loading || surveysLoading || attachmentsLoading || isLoading"
      class="flex flex-1"
    >
      <BaseFlexSpinner size="medium-small" />
    </div>
    <div
      v-else-if="customer"
    >
      <CustomerQuickSurveyTools
        :surveys="surveys ?? []"
        :consumer-key="customer.consumer_key"
        :attachments="attachments ?? []"
        @survey-completed="refreshInstances"
      />
      <CustomerLaboratoryFlowcharts
        :laboratories="laboratoriesToDisplay as Laboratory[]"
        :surveys="surveys ?? []"
        :consumer-key="customer.consumer_key"
        @survey-completed="refreshInstances"
      />
      <div class="flex flex-1 flex-col mt-4 space-y-2">
        <div class="flex items-center">
          <h1>
            Résultats
          </h1>
          <div
            v-if="instances.refreshing"
            class="ml-2"
          >
            <BaseSpinner size="button" />
          </div>
        </div>
        <div
          v-if="instances?.survey?.length || instances?.flowchart?.length"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 flex-1 gap-5 pb-2"
        >
          <CustomerSurveyCard
            v-for="instance in instances.survey"
            :key="`instance-${instance.id}`"
            :instance="instance"
            @deleted="refreshInstances"
          />
          <FlowchartInstanceCard
            v-for="instance in instances.flowchart"
            :key="`flowchart-instance-${instance.id}`"
            :instance="instance"
            @deleted="refreshInstances"
          />
        </div>
        <div
          v-else
          class="text-sm ml-6"
        >
          Aucun résultat disponible pour le moment.
        </div>
      </div>
    </div>
  </BaseContainer>
</template>

<script lang="ts" setup>
import {storeToRefs} from "pinia";
import {usePatientStore} from "@/stores/patient.store";
import useSwr from "@/core/composables/swr/useSwr";
import {attachmentApi, flowchartInstanceApi, laboratoryApi, surveyAnswerInstanceApi, surveyApi} from "@/container";
import {computed, onMounted, reactive, watch} from "vue";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import BaseSpinner from "@/core/components/base/spinner/BaseSpinner.vue";
import CustomerSurveyCard from "@/customer/components/customer-surveys/CustomerSurveyCard.vue";
import FlowchartInstanceCard from "@/flowcharts/Components/FlowchartInstanceCard.vue";
import CustomerQuickSurveyTools from "@/customer/components/customer-surveys/CustomerQuickSurveyTools.vue";
import {useLaboratoriesStore} from "@/laboratory/stores/laboratories.store";
import {Laboratory} from "@/core/interfaces/laboratory/Laboratory";
import CustomerLaboratoryFlowcharts from "@/customer/components/customer-surveys/CustomerLaboratoryFlowcharts.vue";
import {cacheKey} from "@/lib/cache";

const patientStore = usePatientStore();
const {customer} = storeToRefs(patientStore);
const {laboratories} = storeToRefs(useLaboratoriesStore());
const {data, isLoading} = useSwr(
    'partnership/patient/laboratories',
    () => laboratoryApi().patientPartnerLaboratories()
);
const {data: attachments, isLoading: attachmentsLoading} = useSwr(
    () => cacheKey('prefill-attachments'),
    () => attachmentApi().prefillIndex()
);

const {data: surveys, isLoading: surveysLoading} = useSwr("surveys", () => surveyApi().index().then((res) => res.data));

const instances = reactive<{
    meta: null | any,
    survey: null | any[],
    flowchart: null | any[],
    loading: boolean,
    refreshing: boolean
}>({
    meta: null,
    survey: null,
    flowchart: null,
    loading: false,
    refreshing: false
});

onMounted(async () => {
    instances.loading = true;
    await loadInstances();
    instances.loading = false;
});

/**
 * When data is fetched, we populate the laboratories store with the fetched data
 */
watch(() => data.value, (newValue) => {
    if (newValue) {
        laboratories.value = newValue.reduce((acc, laboratory) => {
            acc[laboratory.id] = laboratory;
            return acc;
        }, {});
    }
});

/**
 * Filter laboratories to display only those which have a survey that contains a flowchart with a matching laboratory_id.
 */
const laboratoriesToDisplay = computed(() => {
    if (!laboratories.value || !surveys.value) {
        return [];
    }

    return Object.values(laboratories.value).filter(laboratory => {
        return surveys.value?.some(survey => {
            return survey.flowchart?.laboratory_id === (laboratory as Laboratory).id;
        });
    });
});

/**
 * Load the survey instances and flowchart instances for the customer.
 */
const loadInstances = async (page: number = 1) => {
    if (customer.value?.consumer_key) {
        await surveyAnswerInstanceApi()
            .answeredInstance({consumer_key: customer.value.consumer_key, page})
            .then(response => {
                instances.meta = response.meta;
                instances.survey = (!instances.survey || page === 1)
                    ? response.data
                    : [...instances.survey, ...response.data];
            });

        await flowchartInstanceApi().index({consumer_key: customer.value.consumer_key})
            .then(response => {
                instances.flowchart = (!instances.flowchart || page === 1)
                    ? response.data
                    : [...instances.flowchart, ...response.data];
            });
    } else {
        return Promise.resolve();
    }
};

/**
 * Refresh the customer instances.
 */
const refreshInstances = () => {
    instances.refreshing = true;
    loadInstances()
        .finally(() => {
            instances.refreshing = false;
        });
};
</script>
